// © Microsoft Corporation. All rights reserved.

import { ConfigReference } from "@msnews/config-data-types";
import { ChildExperienceConfigInfo, DynamicBundles, getConfigId, getExperienceType, RequestContext, resolver } from "@msnews/core";
import { AppError, LoadDataConnectorFailed, logException } from "@msnews/diagnostics";
import { ConnectorNamespaces } from "@msnews/experiences-constants";
import { IDataConnector } from "@msnews/experiences-redux-contracts";
import { PageBase } from "@msnews/experiences-page-base";
import { canUseDOM } from "@msnews/isomorphic-util";
import { reportDataConnectorStateChange } from "@msnews/experiences-dynamic-reducer";

let dataConnectorLoadPromiseMap: { [experienceType: string]: Promise<any> } = {};

/**
 * Gets a data connector with the given namespace.
 * @param namespace The data connector's namespace.
 * @returns The data connector.
 */
export async function getDataConnector<T extends IDataConnector<any, any>>(namespace: ConnectorNamespaces): Promise<T> {
    return namespace
        ? <T>(await PageBase.getInstance().rootReducer.getDataConnector(namespace))
        : null;
}

/**
 * Tries to get a data connector with the given namespace.
 * @param namespace The data connector's namespace.
 * @returns The data connector, or null if it could not be found.
 */
export function tryGetDataConnector<T extends IDataConnector<any, any>>(namespace: ConnectorNamespaces): T {
    return <T>PageBase.getInstance().rootReducer.connector(namespace);
}

/**
 * Loads the data connector for the given config reference.
 * @param configInfo The config info for the data connector to load.
 */
export async function loadDataConnector(configInfo: ChildExperienceConfigInfo): Promise<void> {
    const experienceType = getExperienceType(configInfo);

    // If loadDataConnector was already called, return existing promise.
    let promise = dataConnectorLoadPromiseMap[experienceType];
    if (promise) {
        return promise;
    }

    promise = new Promise<void>(async (resolve, reject) => {
        try {
            const experienceType = getExperienceType(configInfo);
            const configId: string | ConfigReference = getConfigId(configInfo);

            // Warm the config and get the module definition in parallel
            const configPromise = resolver.getConfig(configId);
            const modulePromise = DynamicBundles.loadExperience(experienceType);
            const promiseResults = await Promise.all([configPromise, modulePromise]);

            const config = promiseResults[0].properties;
            const module = promiseResults[1];

            // Get the Connector/Reducer/Component via naming convention
            const ExperienceConnector = module[experienceType + "Connector"];
            const ExperienceReducer = module[experienceType + "Reducer"];
            const app = PageBase.getInstance();

            // Data connectors are singletons, so their registered namespace is their experienceType.
            // This allows them to be found using their ExperienceIds enum.
            const experienceConnector = new ExperienceConnector(
                experienceType,
                "",
                app.rootReducer,
                new ExperienceReducer(config),
                app.store,
                config,
                new RequestContext()
            );

            // Report initial data connector state when loaded.
            if (canUseDOM()) {
                reportDataConnectorStateChange(experienceType, experienceConnector.getCurrentState());
            }
        } catch (error) {
            const loadDataConnectorFailed: AppError = { ...LoadDataConnectorFailed, source: "GetDataConnector.ts" };
            logException(error, loadDataConnectorFailed, "Loading data connector failed.");
        }

        // Resolve the promise irrespective of any exception thrown so that other configured data connectors can still load.
        // If an exception is thrown, it will fire an app error.
        resolve();
    });

    dataConnectorLoadPromiseMap[experienceType] = promise;
    return promise;
}

/**
 * Clears out any loaded maps. Use only when testing. Will be tree-shaken away for prod.
 */
export function clearLoadedMapsTest() {
    dataConnectorLoadPromiseMap = {};
}